/**
 * src/shell/gm-client/save-cache.ts — React 侧存档快照缓存。
 *
 * useSyncExternalStore 需要同步快照：启动时先灌缓存（main.tsx await），
 * 之后 store 广播 → 异步重读 → 换对象引用 → React 重渲染。
 * 候选（外壳键）与引擎事件变化同样触发刷新（事件流是落盘变化的最快信号）。
 */

import { subscribeEvents, type SaveBundle } from '../../gm'
import { readCandidates } from '../save/candidates'
import { localSaveStore, subscribeSave } from '../save/store'
import type { Employee } from '../../gm'

export interface SaveSnapshot {
  bundle: SaveBundle
  candidates: Employee[]
  version: number
}

let snapshot: SaveSnapshot | null = null

type Listener = () => void
const listeners = new Set<Listener>()

/** 启动时调用一次（await），保证首帧渲染有数据 */
export async function initSaveCache(): Promise<void> {
  await refresh()
  subscribeSave(() => void refresh())
  // 引擎写存档与壳广播同源（同一端口），事件订阅是兜底（跨标签页 storage 事件已覆盖，双保险）
  subscribeEvents(() => void refresh())
}

async function refresh(): Promise<void> {
  const bundle = await localSaveStore.readAll()
  snapshot = {
    bundle,
    candidates: readCandidates(),
    version: (snapshot?.version ?? 0) + 1,
  }
  for (const l of listeners) l()
}

export function subscribeSnapshot(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function getSnapshot(): SaveSnapshot {
  if (!snapshot) throw new Error('save-cache 未初始化——main.tsx 应先 await initSaveCache()')
  return snapshot
}
